import { GroupByDateFormat } from "@/types/insights/groupByDateFormat"
import { SearchClauseParameters, View } from "@/types/view"
import { CaseColumn } from "../caseColumns"
import { InsightCalculation, CalculationType, CalculationSortItemType, InsightCalculationTotal, InsightCalculationResult, InsightCalculationColumn } from "./calculation"
import { InsightSearch, SearchField, SearchColumn } from "./search"
import { DataType, Field } from "@/types"
import { inlineEditMode } from "@/types/insights/selectiveInlineEdit"
import { FieldTypeId } from "../fieldTypes"
import { InsightFormattingOptions } from "./formattingOptions"

export { InsightCalculation, CalculationType, CalculationSortItemType, InsightCalculationTotal, InsightCalculationResult }
export { InsightSearch, SearchField, SearchColumn }

export { Operator, operatorLabels, operatorsByFieldType, dataTypeTests } from "./operators"
export { DataType, dataTypeByFieldType } from "../dataType"
export { LabelFormat } from "./labelFormat"
export { FormattingRule } from "./formattingRule"
export { GroupByDateFormat } from "./groupByDateFormat"

export interface Insight extends View {
	displayColumns?: any [];
	defaultView?: string | null

	lastUpdated?: Date;

	calculations: InsightCalculation[]
	calculationTotals?: InsightCalculationTotal[]
	calculationSort?: string[] | null

	groupBy?: string | null
	groupByDateFormat?: GroupByDateFormat | null

	groupingOptions?: { [key: string]: string }

	showCalculatedDateRange?: boolean | null
	showCalculatedUserStatus?: boolean | null

	includeCalculationsFrom?: string[] | null

	sortByDataType?: GroupByDateFormat | null
	sortByDescending?: boolean

	dynamicParameters?: DynamicParameter[] | null
	includeGlobalParameters?: string[] | null

	includeForecast?: boolean
	forecastBy?: string | null

	defaultTimeZone?: string | null

	formattingOptions?: InsightFormattingOptions | null
	formattingOptionsAdHoc?: InsightFormattingOptions | null

	presetKey?: string | null
	presetParameters?: { [key: string]: unknown } | null
}

export interface InsightSummary {
	includeGlobalParameters: any
    id: string
    tenantId: string
    title: string
    folder: string
    teams: any[]
	groupBy: string | null
	hasCalculations: boolean
}

export interface InsightDetails extends Insight {
	calculations: InsightCalculation[]
	calculationColumns: InsightCalculationColumn[]
	displayColumns?: CaseColumn[]
	calculationSort: string[] | null

	groupByApplyFilters?: boolean
}

export interface DynamicParameter {
	dataType: DataType
	key: string
	name: string
	dataSourceId: string | null
	passValueToParameter?: string[]

	defaultValue?: any
	searchClauseParameters?: SearchClauseParameters | null
	dynamicValue?: boolean
}

export interface DynamicParameterValue {
	dataType: DataType
	key: string
	value: any
	dynamicValue: boolean
	passValueToParameter?: string[]
	searchClauseParameters?: SearchClauseParameters | null
}

export interface ColumnOptions {
	inlineEdit: boolean
}

export const editableFieldTypes = new Set<string>([
	FieldTypeId.Text, FieldTypeId.TextArea, FieldTypeId.Checkbox, FieldTypeId.Number, FieldTypeId.Dollar, FieldTypeId.Date,
	FieldTypeId.DateTime, FieldTypeId.UserRole, FieldTypeId.List, FieldTypeId.ListMultiple, FieldTypeId.ExternalLink, FieldTypeId.ListData
])

export enum InsightViewType {
	Table = "table",
	HeatMap = "HeatMap",
	CalculationTable = "CalculationTable",
	ColumnChart = "ColumnChart",
	BarChart = "BarChart",
	ComboChart = "ComboChart",
	PieChart = "PieChart",
	LineChart = "LineChart"
}

export const insightViews = [
	{
		key: InsightViewType.Table,
		label: "Table View",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => true
	},
	{
		key: InsightViewType.HeatMap,
		label: "Heat Map",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => insight.columns.some((column: string) => {
			var columnField: any
			if (usingDashboard) {
				columnField = (fields as Field[]).find(f => column === "fields." + f.id)
			} else {
				columnField = (fields as CaseColumn[]).find(f => column === "fields." + f.fieldId)
			}
			return columnField && columnField.dataType === "map-marker"
		})
	},
	{
		key: InsightViewType.CalculationTable,
		label: "Calculation Table",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => insight.groupBy && calculations.length > 0
	},
	{
		key: InsightViewType.ColumnChart,
		label: "Bar Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0
	},
	{
		key: InsightViewType.BarChart,
		label: "Horizontal Bar Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: any) => calculations.length > 0 && usingDashboard
	},
	{
		key: InsightViewType.ComboChart,
		label: "Combo Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0
	},
	{
		key: InsightViewType.PieChart,
		label: "Pie Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0 && !calculations[0].groupBy
	},
	{
		key: InsightViewType.LineChart,
		label: "Line Chart",
		canDisplay: (insight: any, calculations: InsightCalculation[], fields: CaseColumn[] | Field[], usingDashboard: boolean) => calculations.length > 0
	}
]

export const createDefaultInsight = (): InsightDetails => {
	return {
		id: undefined,
		clauses: [],
		groupBy: "",
		calculations: [],
		columns: [
			"title",
			"templateId"
		],
		calculationColumns: [],
		title: "",
		folder: "",
		teams: [],
		viewTeams: [],
		sortBy: "",
		groupByDataType: null,
		groupByOption: null,
		tenantId: "",
		calculationTotals: [],
		sortByDescending: false,
		includeForecast: false,
		inlineEditMode: inlineEditMode.None,
		calculationSort: null
	}
}
